import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, TextField, Tooltip, Typography } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import React from 'react';
import UserFetch from '../../userFetch';

export interface BuilderTokenInfo {
  id: number;
  user_uuid: string;
  email: string;
  label: string | null;
  last_seen: string | null;
  last_ip: string | null;
  revoked: boolean;
  created: string;
  connected: boolean;
}

interface Props {
  open: boolean;
  userUuid: string;
  userEmail: string;
  existing: BuilderTokenInfo | null;
  onClose: () => void;
  onChanged: () => void;
}

function BuilderTokenDialog({ open, userUuid, userEmail, existing, onClose, onChanged }: Props) {
  const [label, setLabel] = React.useState('');
  const [newToken, setNewToken] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [confirmRegen, setConfirmRegen] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      setLabel(existing?.label || '');
      setNewToken(null);
      setError(null);
      setConfirmRegen(false);
    }
    // Intentionally only depends on `open` — we initialize state once when the dialog opens.
    // `existing` updates from parent refetches must NOT wipe the freshly-generated raw token.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const create = () => {
    setBusy(true);
    setError(null);
    UserFetch(`/api/builder-tokens/${userUuid}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ label: label || null }),
    })
      .then(async (r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const data = await r.json();
        setNewToken(data.token);
        onChanged();
      })
      .catch((e) => setError(e.message))
      .finally(() => setBusy(false));
  };

  const revoke = () => {
    setBusy(true);
    setError(null);
    UserFetch(`/api/builder-tokens/${userUuid}`, { method: 'DELETE' })
      .then((r) => {
        if (!r.ok && r.status !== 404) throw new Error(`HTTP ${r.status}`);
        onChanged();
        onClose();
      })
      .catch((e) => setError(e.message))
      .finally(() => setBusy(false));
  };

  const copyToken = () => {
    if (newToken) navigator.clipboard.writeText(newToken);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Builder Token — {userEmail}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {newToken ? (
          <Box>
            <Alert severity="warning" sx={{ mb: 2 }}>
              Copy this token now. It will not be shown again.
            </Alert>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TextField
                value={newToken}
                fullWidth
                InputProps={{ readOnly: true, sx: { fontFamily: 'monospace', fontSize: 12 } }}
              />
              <Tooltip title="Copy">
                <IconButton onClick={copyToken}><ContentCopyIcon /></IconButton>
              </Tooltip>
            </Box>
            <Typography variant="body2" sx={{ mt: 2 }}>
              Paste into <code>%USERPROFILE%\.iot-builder\config.json</code> under the active environment.
            </Typography>
          </Box>
        ) : existing ? (
          <Box>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Status: {existing.connected ? <b style={{ color: 'green' }}>● Connected</b> : existing.revoked ? <b style={{ color: 'red' }}>Revoked</b> : <b style={{ color: 'gray' }}>○ Offline</b>}
            </Typography>
            <Typography variant="body2">Created: {new Date(existing.created).toLocaleString()}</Typography>
            <Typography variant="body2">Last seen: {existing.last_seen ? new Date(existing.last_seen).toLocaleString() : 'never'}</Typography>
            <Typography variant="body2">Last IP: {existing.last_ip || '-'}</Typography>
            <TextField
              label="Label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              size="small"
              fullWidth
              sx={{ mt: 2 }}
            />
            {confirmRegen && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                Regenerating invalidates the existing token. The builder will need its config updated.
              </Alert>
            )}
          </Box>
        ) : (
          <Box>
            <Typography variant="body2" sx={{ mb: 2 }}>
              No builder token exists for this user. Generate one to allow the iot-builder daemon to connect.
            </Typography>
            <TextField
              label="Label (optional)"
              placeholder="e.g. Norm's PC"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              size="small"
              fullWidth
              autoFocus
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        {existing && !newToken && (
          <Button color="error" onClick={revoke} disabled={busy}>Revoke</Button>
        )}
        {existing && !newToken && (
          confirmRegen ? (
            <Button variant="contained" color="warning" onClick={create} disabled={busy}>Confirm Regenerate</Button>
          ) : (
            <Button variant="contained" onClick={() => setConfirmRegen(true)} disabled={busy}>Regenerate</Button>
          )
        )}
        {!existing && !newToken && (
          <Button variant="contained" onClick={create} disabled={busy}>Generate Token</Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default BuilderTokenDialog;
